import { useState } from "react";
import { closeModal, Modal, Select } from "risa-oasis-ui_v2";
import type { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import { ModalId } from "../../../../../enums/modalId";
import type { PocUserFormData } from "../data-model/pocAssignmentModel";
import type { PocNameOption } from "../hooks/usePocNameOptions";
import {
  AVAILABILITY_BADGE_LIST,
  COHORT_OPTION_VALUES,
} from "../utils/pocAssignmentConstants";

interface AddNewUserModalProps {
  /** POC dropdown options shaped as { label: name, value: id }. */
  pocNameOptions: PocNameOption[];
  /** Group dropdown options for the Group field. */
  groupOptions: BadgeWithIconProps[];
  /** True while group options are loading from Firestore metadata. */
  groupOptionsLoading?: boolean;
  onSave: (data: PocUserFormData) => void;
}

const EMPTY_FORM: PocUserFormData = {
  pocName: "",
  group: "",
  availability: "",
  cohort: "",
};

const GROUP_OPTIONS = (groupOptions: BadgeWithIconProps[]) =>
  groupOptions.map((badge) => ({
    label: badge.text,
    value: badge.id,
  }));

const AVAILABILITY_OPTIONS = AVAILABILITY_BADGE_LIST.map((badge) => ({
  label: badge.text,
  value: badge.id,
}));

const COHORT_OPTIONS = COHORT_OPTION_VALUES.map((value) => ({
  label: value,
  value,
}));

/**
 * Modal for adding a new POC row to the config table.
 * Opened imperatively with `openModal(ModalId.ADD_NEW_USER_MODAL)`.
 * Group options are supplied by the parent (typically from `poc_assignment_groups` metadata).
 *
 * @param pocNameOptions - POC id/name pairs for the POC Name field.
 * @param groupOptions - Group badge options for the Group dropdown.
 * @param groupOptionsLoading - When true, Save stays disabled until options finish loading.
 * @param onSave - Persists the new row into parent table state.
 */
const AddNewUserModal = ({
  pocNameOptions,
  groupOptions,
  groupOptionsLoading = false,
  onSave,
}: AddNewUserModalProps) => {
  const [form, setForm] = useState<PocUserFormData>(EMPTY_FORM);

  const updateField = (field: keyof PocUserFormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const close = () => {
    setForm(EMPTY_FORM);
    closeModal(ModalId.ADD_NEW_USER_MODAL);
  };

  const handleSave = () => {
    onSave(form);
    close();
  };

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_USER_MODAL}
      title="Add New User"
      /** Let `handleSave` call `close()` after `setForm(EMPTY_FORM)` so fields do not persist on reopen. */
      autoCloseOnSave={false}
      onClose={close}
      onCancel={close}
      onSave={handleSave}
      saveButtonText="Save"
      cancelText="Cancel"
      disableSave={
        !form.pocName ||
        !form.group ||
        groupOptionsLoading ||
        groupOptions.length === 0
      }
    >
      <div className="flex flex-col gap-4 py-2">
        <Select
          id="add-user-poc-name"
          label="POC Name"
          placeholder="Select POC Name"
          options={pocNameOptions}
          value={form.pocName}
          onOptionChange={(option) => updateField("pocName", option.value)}
        />
        <Select
          id="add-user-group"
          label="Group"
          placeholder="Select Group"
          options={GROUP_OPTIONS(groupOptions)}
          value={form.group}
          onOptionChange={(option) => updateField("group", option.value)}
        />
        <Select
          id="add-user-availability"
          label="Availability"
          placeholder="Select Availability"
          options={AVAILABILITY_OPTIONS}
          value={form.availability}
          onOptionChange={(option) => updateField("availability", option.value)}
        />
        <Select
          id="add-user-cohort"
          label="Cohort"
          placeholder="Select Cohort"
          options={COHORT_OPTIONS}
          value={form.cohort}
          onOptionChange={(option) => updateField("cohort", option.value)}
        />
      </div>
    </Modal>
  );
};

export default AddNewUserModal;
